/**
 * Did anything actually move?
 *
 * Every step in this wizard saves on Continue and on Save &amp; exit, which is right — the client
 * should never lose work by navigating. What is not right is *writing* when nothing changed.
 *
 * A write is not free here. `PUT /campaign-requests/:id` and every nested write under it run
 * `CampaignRequestsService.recompute()`, and `recompute()` calls `supersedeOnEdit()`: on a request
 * sitting in `quoted` or `awaiting_confirmation` that withdraws the live quote, walks the request
 * back to `changes_requested`, and writes "The request was edited after it was priced" onto the
 * client-facing timeline (UX §12). Re-pricing is ours alone — `POST /quote` grants no client role
 * — so a client who re-opened a step and pressed Continue could put their own request into a state
 * only we can get it out of, having changed nothing.
 *
 * So each step compares its draft against what it loaded and skips the mutation when the two
 * agree. These are the comparisons that are worth sharing; anything else is one `!==`.
 */

/** Two lists of the same values in any order. Reordering chips is not an edit. */
export function sameStrings(left: readonly string[], right: readonly string[]): boolean {
	if (left.length !== right.length) return false;
	const sorted = right.toSorted();
	return left.toSorted().every((value, index) => value === sorted[index]);
}

/** Same, for the ISO weekday lists on a market row. */
export function sameNumbers(left: readonly number[], right: readonly number[]): boolean {
	if (left.length !== right.length) return false;
	const sorted = right.toSorted((a, b) => a - b);
	return left.toSorted((a, b) => a - b).every((value, index) => value === sorted[index]);
}

/**
 * A text field against the column behind it.
 *
 * The field holds `""` where the column holds null, and neither of those is a change to the other
 * — which is the comparison that decides whether an untouched step writes.
 *
 * Both empties fold to null, the column's included. On `?? null` an empty-string column read as a
 * change against an empty field forever: the step wrote on every Continue, and every write
 * supersedes a live quote.
 */
export function sameText(field: string, column: string | null | undefined): boolean {
	return (field.trim() || null) === (column || null);
}

/** `HH:MM` from an input against the `HH:MM:SS` Postgres hands back. */
export function sameTime(field: string, column: string | null | undefined): boolean {
	return (field || null) === (column ? column.slice(0, 5) : null);
}
